import type { JSONContent } from "@tiptap/core";
import { PURCHASER_API_BASE_URL, getHeaders } from "../Constants";
import z, { ZodError } from "zod";

export interface DashboardStatsResponse {
    kpis: {
        openRfqs: number;
        awaitingQuotes: number;
        quotesThisPeriod: number;
        avgCycleTimeDays: number | null;
    };
    attentionRequired: {
        overdueRfqs: Array<{
            uniquePublicId: string;
            title: string;
            submissionDeadline: string | null;
            vendorCount: number;
            quoteCount: number;
        }>;
        stalledRfqs: Array<{
            uniquePublicId: string;
            title: string;
            lastActivityAt: string;
            daysSinceActivity: number;
        }>;
        quoteReminders: Array<{
            mailboxId: number;
            vendorName: string;
            rfqPublicId: string;
            rfqTitle: string;
            daysSinceSent: number;
            lastReminderAt: string | null;
        }>;
        unreadCount: number;
        unreadMessages: Array<{
            id: number;
            vendorName: string;
            rfqTitle: string;
            rfqPublicId: string;
            mailboxId: number;
            preview: string;
            timestamp: string;
        }>;
    };
    upcomingDeadlines: Array<{
        uniquePublicId: string;
        title: string;
        submissionDeadline: string;
    }>;
    pipeline: {
        draft: number;
        sent: number;
        quoted: number;
        closed: number;
        rfqs: Array<{
            uniquePublicId: string;
            title: string;
            updatedAt: string;
            vendorCount: number;
            quoteCount: number;
            status: "draft" | "sent" | "quoted" | "closed";
        }>;
    };
    recentActivity: Array<{
        type: "quote_received" | "rfq_closed" | "rfq_sent";
        rfqPublicId: string;
        rfqTitle: string;
        vendorName?: string;
        grandTotal?: number;
        vendorCount?: number;
        mailboxId?: number;
        timestamp: string;
    }>;
    teamWorkload: Array<{
        userId: number;
        userName: string;
        openRfqs: number;
        quotesReceived: number;
    }>;
    monthlyTrends: Array<{
        month: string;
        year: number;
        rfqsCreated: number;
        rfqsClosed: number;
        quotesReceived: number;
        avgCycleTime: number | null;
    }>;
    cycleTimeByMonth: Record<string, Array<{
        uniquePublicId: string;
        title: string;
        createdAt: string;
        closedAt: string;
        cycleTimeDays: number;
    }>>;
    dailyTrends: Array<{
        date: string;
        dayOfMonth: number;
        month: string;
        rfqsCreated: number;
        rfqsClosed: number;
        quotesReceived: number;
    }>;
    communicationStats: {
        messagesSent: number;
        messagesReceived: number;
        messagesRead: number;
    };
}

export type TeamMember = {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    deactivatedAt: string | null;
    iconBackgroundColorClass: string;
};

export type UserInvite = {
    id: number;
    teamId: number;
    emailAddress: string;
    createdAt: string;
};

export type InternalUser = {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
};

export type TeamInfo = {
    teamName: string;
    paidUsers: {
        id: number;
        firstName: string;
        lastName: string;
        email: string;
    }[];
    internalUsers: InternalUser[];
    approvedDomains: {
        id: number;
        domain: string;
    }[];
    invites: {
        id: number;
        teamId: number;
        emailAddress: string;
        createdAt: string;
    }[];
    isInviteGated: boolean;
};

export default {
    getTeamMembers: (): Promise<TeamMember[]> => fetch(`${PURCHASER_API_BASE_URL}/team/members`, { headers: getHeaders() }).then(res => res.json()),

    getUserInvites: (): Promise<UserInvite[]> => fetch(`${PURCHASER_API_BASE_URL}/team/invite`, { headers: getHeaders() }).then(res => res.json()),

    getTeamInfo: async (): Promise<TeamInfo> => {
        const url = `${PURCHASER_API_BASE_URL}/team/info`;
        const res = await fetch(url, { headers: getHeaders() });
        if (!res.ok || res.status !== 200) {
            throw new Error("Failed to get team info");
        }
        return res.json();
    },

    changeTeamName: async (name: string): Promise<void> => {
        const url = `${PURCHASER_API_BASE_URL}/team/info`;
        const res = await fetch(url, {
            method: "PATCH",
            headers: getHeaders(),
            body: JSON.stringify({ name }),
        });

        if (!res.ok && res.status !== 204) {
            throw new Error("Failed to update team name");
        }
    },

    inviteUsers: (emails: string[]) =>
        fetch(`${PURCHASER_API_BASE_URL}/team/invite`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify({ emails }),
        }).then(res => res.json()),

    deleteUserInvite: (id: number) =>
        fetch(`${PURCHASER_API_BASE_URL}/team/invite`, {
            headers: getHeaders(),
            method: "DELETE",
            body: JSON.stringify({ id }),
        }).then(res => res.json()),

    requestFeatureAccess: (params: { featureName: string; userMessage?: string; userProperties?: Record<string, any> }) =>
        fetch(`${PURCHASER_API_BASE_URL}/team/feature-access`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify(params),
        }).then(res => res.json()),

    getUserGreeting: () => fetch(`${PURCHASER_API_BASE_URL}/team/greet`, { headers: getHeaders() }).then(res => res.json()),

    getRecentEntities: () => fetch(`${PURCHASER_API_BASE_URL}/team/recent-entities`, { headers: getHeaders() }).then(res => res.json()),

    getTeamUsage: () => fetch(`${PURCHASER_API_BASE_URL}/team/usage`, { headers: getHeaders() }).then(res => res.json()),

    requestPayByInvoice: (data: { billingContactName: string; billingContactEmail: string }) =>
        fetch(`${PURCHASER_API_BASE_URL}/team/request-pay-by-invoice`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify(data),
        }).then(res => res.json()),

    getHelpArticles: () => {
        return fetch(`https://purchaser.ai/api/featured-docs`, {
            method: "GET",
        }).then(res => {
            if (!res.ok) {
                return [];
            }
            return res.json();
        });
    },

    getNewFeatures: () => {
        return fetch(`https://purchaser.ai/api/new-features`, {
            method: "GET",
        }).then(res => {
            if (!res.ok) {
                return [];
            }
            return res.json();
        });
    },

    getProTips: () => {
        return fetch(`https://purchaser.ai/api/pro-tips`, {
            method: "GET",
        }).then(res => {
            if (!res.ok) {
                return [];
            }
            return res.json();
        });
    },

    getTeamStats: async () => {
        const res = await fetch(`${PURCHASER_API_BASE_URL}/team/stats`, {
            method: "GET",
            headers: getHeaders(),
        });
        if (!res.ok) {
            throw new Error("Failed to get team stats");
        }
        return (await res.json()) as Promise<{
            rfqsSent: {
                value: number;
                vsLast7Days: number;
            };
            quoteCompares: {
                value: number;
                vsLast7Days: number;
            };
            draftRfqs: {
                value: number;
                vsLast7Days: number;
            };
        }>;
    },

    getDashboardStats: async (params?: { userId?: number; days?: number }) => {
        const searchParams = new URLSearchParams();
        if (params?.userId) searchParams.set("userId", params.userId.toString());
        if (params?.days) searchParams.set("days", params.days.toString());

        const queryString = searchParams.toString();
        const url = `${PURCHASER_API_BASE_URL}/team/dashboard-stats${queryString ? `?${queryString}` : ""}`;

        const res = await fetch(url, {
            method: "GET",
            headers: getHeaders(),
        });
        if (!res.ok) {
            throw new Error("Failed to get dashboard stats");
        }
        return (await res.json()) as DashboardStatsResponse;
    },

    sendTestEmail: (data: { emailAddress: string; content: JSONContent; fileUploadIds?: number[] }) =>
        fetch(`${PURCHASER_API_BASE_URL}/team/send-test-email`, {
            headers: getHeaders(),
            method: "POST",
            body: JSON.stringify(data),
        }).then(res => res.json()),

    getCalendarEvents: async (params?: { start?: string; end?: string }) => {
        const searchParams = new URLSearchParams();
        if (params?.start) searchParams.set("start", params.start);
        if (params?.end) searchParams.set("end", params.end);

        const queryString = searchParams.toString();
        const url = `${PURCHASER_API_BASE_URL}/team/calendar-events${queryString ? `?${queryString}` : ""}`;

        const res = await fetch(url, {
            method: "GET",
            headers: getHeaders(),
        });
        if (!res.ok) {
            throw new Error("Failed to get calendar events");
        }
        return (await res.json()) as CalendarEventsResponse;

    },

    addInternalUser: async (payload: { emailAddress: string; firstName: string | null; lastName: string | null }): Promise<void> => {
        const url = `${PURCHASER_API_BASE_URL}/team-colleagues`;
        const res = await fetch(url, { headers: getHeaders(), method: "POST", body: JSON.stringify(payload) });
        if (!res.ok) throw new Error("Failed to add internal user");
    },

    updateInternalUser: async (id: number, payload: { firstName: string | null; lastName: string | null }): Promise<void> => {
        const url = `${PURCHASER_API_BASE_URL}/team-colleagues/${id}`;
        const res = await fetch(url, { headers: getHeaders(), method: "PATCH", body: JSON.stringify(payload) });
        if (!res.ok) throw new Error("Failed to add internal user");
    },

    deleteInternalUser: async (id: number): Promise<void> => {
        const url = `${PURCHASER_API_BASE_URL}/team-colleagues/${id}`;
        const res = await fetch(url, { headers: getHeaders(), method: "DELETE" });
        if (!res.ok) throw new Error("Failed to delete internal user");
    },

    addApprovedDomain: async (domain: string): Promise<void> => {
        try {
            // The backend will do the hard checks but this is a small sanity check before we make the request
            const softValidDomain = z
                .string()
                .min(3)
                .refine(val => val.includes("."), {
                    message: "Domain must contain a dot.",
                })
                .refine(val => val.split(".").filter(s => s !== "").length > 1, {
                    message: "Invalid domain structure.",
                })
                .refine(val => !val.includes(" "), {
                    message: "Domain should not contain spaces.",
                })
                .parse(domain);

            const url = `${PURCHASER_API_BASE_URL}/approved-domains`;
            const res = await fetch(url, { headers: getHeaders(), method: "POST", body: JSON.stringify({ domain: softValidDomain }) });

            if (!res.ok) {
                throw new Error("Error adding approved domain");
            }

            if (res.status < 299) return;

            const body = await res.json();
        } catch (e) {
            if (e instanceof ZodError) {
                throw new Error(e.issues[0].message);
            } else {
                throw e;
            }
        }
    },

    deleteApprovedDomain: async (id: number): Promise<void> => {
        const url = `${PURCHASER_API_BASE_URL}/approved-domains/${id}`;
        const res = await fetch(url, { headers: getHeaders(), method: "DELETE" });

        if (!res.ok) throw new Error("Failed to delete approved domain");

        return res.json();
    },
};

export type CalendarEventType = "rfq_created" | "rfq_sent" | "deadline" | "reminder_scheduled" | "reminder_sent" | "quote_received";

export interface CalendarEvent {
    id: string;
    type: CalendarEventType;
    title: string;
    description: string;
    date: string;
    rfqPublicId?: string;
    mailboxId?: number;
}

export interface CalendarEventsResponse {
    events: CalendarEvent[];
    dateRange: {
        start: string;
        end: string;
    };
}
